/**
 * use-admin-users — admin 账号管理 + audit logs hooks
 */

import type {
  AdminAccountResponse,
  AdminAuditLogResponse,
  AuditLogFacetsResponse,
  UpdateAdminUserRequest,
} from '@novel-admin/shared';
import { type OffsetPagedResult, apiRequest, apiRequestOffsetPaged } from '~/api/client';
import { downloadAuthedCsv } from '~/api/download-authed-csv';

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

export const adminUsersApi = {
  // 团队成员 CSV 导出（root 专属，裸 CSV 流 → downloadAuthedCsv 注 JWT + 触发保存）
  exportCsv: () =>
    downloadAuthedCsv(`${API_BASE || ''}/api/v1/admin-users/export`, 'admin-users.csv'),
  // GET /admin-users 是 offset 分页（okOffsetPaged，默认 limit=200）；admin 列表默认 offset（dev-docs 40-分页与排序）。
  listOffsetPaged: (
    page?: number,
    limit?: number,
  ): Promise<OffsetPagedResult<AdminAccountResponse>> =>
    apiRequestOffsetPaged<AdminAccountResponse>({
      method: 'GET',
      path: '/api/v1/admin-users',
      query: { ...(page ? { page } : {}), ...(limit ? { limit } : {}) },
    }),
  update: (id: string, body: UpdateAdminUserRequest) =>
    apiRequest<AdminAccountResponse>({
      method: 'PATCH',
      path: `/api/v1/admin-users/${id}`,
      body,
    }),
  remove: (id: string) =>
    apiRequest<{ ok: true }>({ method: 'DELETE', path: `/api/v1/admin-users/${id}` }),
};

/**
 * audit-logs 列表查询参数（GET /api/v2/audit-logs，cursor keyset）。
 * 既有维度 resource / resource_id / actor_id + 分页 cursor / limit；
 * 服务端过滤下推（ADM 操作日志筛选，前端不再单页客户端过滤）：
 * - q：action OR resource 模糊搜索（单值）
 * - action：动作多选（重复 query 参数 → string[]）
 * - from / to：created_at 时间窗（ISO 8601 带时区偏移；from ≤ created_at < to）
 * limit 经后端 Zod 校验（非法 → 422），调用方传合法整数或省略。
 */
export type AuditLogsListParams = {
  resource?: string;
  resource_id?: string;
  /** 操作者多选（重复 query 参数 → string[]）；单值兼容。root 专用维度，非 root 被后端 self 覆盖 */
  actor_id?: string | string[];
  page?: number;
  limit?: number;
  q?: string;
  action?: string[];
  from?: string;
  to?: string;
};

export const auditLogsApi = {
  list: (params?: AuditLogsListParams) =>
    apiRequest<AdminAuditLogResponse[]>({
      method: 'GET',
      path: '/api/v2/audit-logs',
      query: params,
    }),
  // GET /audit-logs 是 offset 分页（okOffsetPaged，total/page/limit/hasMore 顶层）；admin 列表默认 offset。
  listOffsetPaged: (
    params?: AuditLogsListParams,
  ): Promise<OffsetPagedResult<AdminAuditLogResponse>> =>
    apiRequestOffsetPaged<AdminAuditLogResponse>({
      method: 'GET',
      path: '/api/v2/audit-logs',
      query: params,
    }),
  // 操作日志筛选 facet（全量 DISTINCT 动作 + 操作员；按角色收敛）：前端下拉取全量选项，替代按当前页去重
  facets: () =>
    apiRequest<AuditLogFacetsResponse>({ method: 'GET', path: '/api/v2/audit-logs/facets' }),
  // 操作日志 CSV 导出（裸 CSV 流，按角色收敛同 list）：透传当前筛选（resource/action/from/to/q/actor_id）
  exportCsv: (params?: AuditLogsListParams) => {
    const qs = new URLSearchParams();
    if (params?.resource) qs.set('resource', params.resource);
    if (params?.resource_id) qs.set('resource_id', params.resource_id);
    for (const id of Array.isArray(params?.actor_id)
      ? params.actor_id
      : params?.actor_id
        ? [params.actor_id]
        : [])
      qs.append('actor_id', id);
    if (params?.q) qs.set('q', params.q);
    if (params?.from) qs.set('from', params.from);
    if (params?.to) qs.set('to', params.to);
    for (const a of params?.action ?? []) qs.append('action', a);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return downloadAuthedCsv(
      `${API_BASE || ''}/api/v2/audit-logs/export${suffix}`,
      'audit-logs.csv',
    );
  },
};
